import {
    InvoiceByCounterpartySortValue,
    InvoiceSortValue,
    InvoiceViewOptionGroup,
} from "@common/types/invoice";
import { InvoiceFilters } from "@common/contracts/invoice.contract";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useInvoicesPageSortingCache } from "@hooks/view/useInvoicesPageSortingCache";
import { useInvoicesByCounterpartyPageSortingCache } from "@hooks/view/useInvoicesByCounterpartyPageSortingCache";
import {
    getInvoiceExportFilename,
    getWorkspaceExportDate,
} from "@common/helpers/spreadsheet/workspaceListExportFilename";
import { saveBlobResponse, useFileDownload } from "@lib/fileDownload";
import { getInvoicesXlsx } from "@lib/invoices";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { DownloadIcon } from "@icons";
import { toInvoiceApiFilters } from "../../contexts/InvoicesViewContext";
import { getSortExpression as getInvoiceSortExpression } from "./useSort";
import { getSortExpression as getCustomerSortExpression } from "./useSortByCounterparty";

interface Props {
    filters: InvoiceFilters | undefined;
    groupBy: InvoiceViewOptionGroup;
    columns: string[];
}

function getExportSort({
    groupBy,
    invoiceSort,
    customerSort,
}: {
    groupBy: InvoiceViewOptionGroup;
    invoiceSort: InvoiceSortValue | undefined;
    customerSort: InvoiceByCounterpartySortValue | undefined;
}) {
    if (groupBy === InvoiceViewOptionGroup.COUNTERPARTY) {
        return getCustomerSortExpression(
            customerSort ?? InvoiceByCounterpartySortValue.OPEN_DESC,
        );
    }

    return getInvoiceSortExpression(invoiceSort ?? InvoiceSortValue.DATE_DESC);
}

export const InvoicesExport: React.FC<Props> = ({
    filters,
    groupBy,
    columns,
}) => {
    const activeWorkspace = useActiveWorkspace();
    const [cachedInvoiceSort] = useInvoicesPageSortingCache();
    const [cachedCustomerSort] = useInvoicesByCounterpartyPageSortingCache();
    const { download, isDownloading } = useFileDownload(async () => {
        if (!activeWorkspace) {
            return;
        }

        const response = await getInvoicesXlsx({
            workspaceId: activeWorkspace.id,
            filters: toInvoiceApiFilters(filters),
            groupBy,
            columns,
            sort: getExportSort({
                groupBy,
                invoiceSort: cachedInvoiceSort,
                customerSort: cachedCustomerSort,
            }),
        });
        saveBlobResponse(
            response,
            getInvoiceExportFilename({
                workspaceName: activeWorkspace.name,
                groupBy,
                date: getWorkspaceExportDate({
                    timezone: activeWorkspace.timezone,
                }),
            }),
        );
    });

    return (
        <ButtonWithLoader
            variant="primary"
            iconOnly
            size="md"
            aria-label="Download invoices as Excel"
            loading={isDownloading}
            onClick={() => {
                void download();
            }}
        >
            <DownloadIcon />
        </ButtonWithLoader>
    );
};
